/**
 * SearchPathContext - Wayfinder for "rabbit hole" search navigation
 * 
 * Tracks the user's search journey so they can:
 * 1. See where they've been (breadcrumb trail)
 * 2. Go BACK to any previous search
 * 3. Resume a search path later
 * 
 * Also stores paths for analytics - understanding how users search
 * can reveal valuable "agentic paths" that could be automated.
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// import api from '../utils/axios'; // Disabled - analytics tracking not active yet

// =============================================================================
// CONTEXT
// =============================================================================
const SearchPathContext = createContext(null);

export const useSearchPath = () => {
  const context = useContext(SearchPathContext);
  if (!context) {
    console.warn('useSearchPath must be used within SearchPathProvider');
    return { 
      path: [], 
      addStep: () => {}, 
      goBack: () => {}, 
      goToStep: () => {},
      clearPath: () => {},
      currentStep: null,
    };
  }
  return context;
};

// =============================================================================
// PATH STEP TYPE
// =============================================================================
// Each step in the search journey
// {
//   id: string,           // Unique ID
//   type: 'search' | 'quick_search' | 'filter' | 'view',
//   query: string,        // The search query
//   mode: string,         // Search mode (candidate, client, company, etc.)
//   source: {             // What triggered this step
//     type: 'company' | 'person' | 'job' | 'skill' | 'industry' | 'manual',
//     name: string,       // Display name (e.g., "Amazon", "VP Sales")
//     data: object,       // Full context data
//   },
//   results: number,      // Number of results found
//   timestamp: number,    // When this step occurred
//   savedSearchId: number | null, // If this was saved as an agent
// }

// =============================================================================
// PROVIDER
// =============================================================================
export function SearchPathProvider({ children }) {
  const navigate = useNavigate();
  const [path, setPath] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [sessionId] = useState(() => `path_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);
  
  // Load path from sessionStorage on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('eq_search_path');
      if (stored) {
        const { path: storedPath, index } = JSON.parse(stored);
        if (Array.isArray(storedPath) && storedPath.length > 0) {
          setPath(storedPath);
          setCurrentIndex(index ?? storedPath.length - 1);
        }
      }
    } catch (e) {
      console.warn('Failed to load search path:', e);
    }
  }, []);
  
  // Persist path to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem('eq_search_path', JSON.stringify({ path, index: currentIndex }));
    } catch (e) {
      console.warn('Failed to save search path:', e);
    }
  }, [path, currentIndex]);
  
  // Current step
  const currentStep = path[currentIndex] || null;
  
  // Add a new step to the path
  const addStep = useCallback((step) => {
    const newStep = {
      id: `step_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      timestamp: Date.now(),
      ...step,
    };
    
    // If we're not at the end, truncate forward history
    setPath(prev => {
      const newPath = [...prev.slice(0, currentIndex + 1), newStep];
      // Limit to last 20 steps
      return newPath.slice(-20);
    });
    setCurrentIndex(prev => Math.min(prev + 1, 19));
    
    // Track for analytics (fire and forget)
    trackStepForAnalytics(newStep);
    
    return newStep;
  }, [currentIndex]);
  
  // Go back one step
  const goBack = useCallback(() => {
    if (currentIndex > 0) {
      const prevStep = path[currentIndex - 1];
      setCurrentIndex(currentIndex - 1);
      
      // Navigate to the previous search
      if (prevStep) {
        navigate('/app', {
          state: {
            query: prevStep.query,
            mode: prevStep.mode,
            fromPath: true,
            pathStepId: prevStep.id,
          }
        });
      }
      return prevStep;
    }
    return null;
  }, [currentIndex, path, navigate]);
  
  // Go to a specific step
  const goToStep = useCallback((stepId) => {
    const stepIndex = path.findIndex(s => s.id === stepId);
    if (stepIndex >= 0) {
      const step = path[stepIndex];
      setCurrentIndex(stepIndex);
      
      navigate('/app', {
        state: {
          query: step.query,
          mode: step.mode,
          fromPath: true,
          pathStepId: step.id,
        }
      });
      return step;
    }
    return null;
  }, [path, navigate]);
  
  // Clear the path (start fresh)
  const clearPath = useCallback(() => {
    setPath([]);
    setCurrentIndex(-1);
    sessionStorage.removeItem('eq_search_path');
  }, []);
  
  // Track step for analytics - DISABLED until backend route is deployed
  const trackStepForAnalytics = (_step) => {
    // TODO: Re-enable after backend restart
    // Analytics tracking disabled to prevent 404 errors
  };
  
  // Check if we can go back
  const canGoBack = currentIndex > 0;
  
  // Check if we can go forward (if user went back)
  const canGoForward = currentIndex < path.length - 1;
  
  // Go forward (if user went back then wants to go forward)
  const goForward = useCallback(() => {
    if (currentIndex < path.length - 1) {
      const nextStep = path[currentIndex + 1];
      setCurrentIndex(currentIndex + 1);
      
      if (nextStep) {
        navigate('/app', {
          state: {
            query: nextStep.query,
            mode: nextStep.mode,
            fromPath: true,
            pathStepId: nextStep.id,
          }
        });
      }
      return nextStep;
    }
    return null;
  }, [currentIndex, path, navigate]);
  
  return (
    <SearchPathContext.Provider value={{
      path,
      currentStep,
      currentIndex,
      addStep,
      goBack,
      goForward,
      goToStep,
      clearPath,
      canGoBack,
      canGoForward,
      sessionId,
    }}>
      {children}
    </SearchPathContext.Provider>
  );
}

export default SearchPathContext;

